import { useState, useEffect, useRef, useMemo, FormEvent } from "react";
import {
  getFacetsQueryString,
  getSearchQueryString,
  getPageNrQueryString,
} from "./utils/queryString";
import { Results } from "./results/Results";
import { Search } from "./search/Search";
import { ServerErrors } from "./server-errors/ServerErrors";
import { Pagination } from "./pagination/Pagination";
import { Sidebar, sidebarHasContent } from "./sidebar/Sidebar";
import { Data, FacetType, PageData, ResourcedTexts } from "./SearchFilterResultData";
import { placeFocusInContainer } from "./utils/placeFocusInContainer";
import { resetFacet } from "./facet/Facet";

type SearchFilterResultProps = {
  data: Data;
  searchTerm: string;
  endPointUrl: string;
  pageData: PageData;
  resourcedTexts: ResourcedTexts;
  sidebarActive: boolean;
  showSearchBoxSelect: boolean;
};

type StateType = {
  facetsQueryString: string;
  searchQueryString: string;
  pageNrQueryString: string;
  searchTerm: string;
  data: Data;
  itemsPerPage: number;
  sortOrder: string[];
};

export default function SearchFilterResult({
  data,
  searchTerm,
  endPointUrl,
  pageData,
  resourcedTexts,
  sidebarActive,
  showSearchBoxSelect,
}: SearchFilterResultProps) {
  const sfrWrapperRef = useRef<HTMLDivElement>(null);

  const [state, setState] = useState<StateType>({
    facetsQueryString: getFacetsQueryString(data.facets),
    searchQueryString: getSearchQueryString(searchTerm),
    pageNrQueryString: getPageNrQueryString(data.pageNr),
    searchTerm: searchTerm,
    data: data,
    itemsPerPage: pageData.itemsPerPage,
    sortOrder: pageData.sortOrder,
  });

  const [loading, setLoading] = useState(false);
  const sidebarRef = useRef<HTMLDivElement>(null);
  const renderSidebar = useMemo(
    () => sidebarActive && sidebarHasContent(data, pageData),
    [sidebarActive, data, pageData]
  );

  useEffect(() => {
    historyPush({ ...state });

    if (typeof window !== "undefined" && history && history.state !== null) {
      window.onpopstate = () => {
        if (history.state !== null && history.state.historyData !== null) {
          setState((prevState) => ({
            ...prevState,
            facetsQueryString: getFacetsQueryString(history.state.historyData.data.facets),
            searchQueryString: getSearchQueryString(history.state.historyData.searchTerm),
            pageNrQueryString: getPageNrQueryString(history.state.historyData.data.pageNr),
            searchTerm: history.state.historyData.searchTerm,
            data: history.state.historyData.data,
          }));
        }
      };
    }
  }, []);

  function getUrlFromQueryStrings(newState: StateType): string {
    const baseUrl = typeof window !== "undefined" ? window.location.href.split("?")[0] : "";

    const queryStringArray = [
      newState.searchQueryString,
      newState.facetsQueryString,
      newState.pageNrQueryString,
    ];

    let urlString: string;

    if (queryStringArray.every((string) => !string)) {
      urlString = baseUrl;
    } else {
      urlString = "?";

      queryStringArray.forEach((string, index) => {
        if (string) {
          urlString = urlString + string + "&";
        }

        const isLastString = index === queryStringArray.length - 1;
        if (isLastString) {
          urlString = urlString.slice(0, -1); // Remove trailing &-sign for a clean url
        }
      });
    }

    return urlString;
  }

  function historyPush(newState: StateType) {
    history.pushState(
      {
        historyData: {
          searchTerm: newState.searchTerm,
          data: newState.data,
        },
      },
      "",
      getUrlFromQueryStrings(newState)
    );
  }

  function handleFacetChange(newFacet: FacetType) {
    const newFacets = state.data.facets.map((facet) =>
      facet.id === newFacet.id ? newFacet : facet
    );

    const newState: StateType = {
      ...state,
      data: { ...state.data, facets: newFacets },
      facetsQueryString: getFacetsQueryString(newFacets),
    };

    resetPageNrAndRequestNewData(newState);
  }

  function handleClearFilter() {
    const newState = {
      ...state,
      facetsQueryString: "",
    };

    const newFacets = newState.data.facets.map(resetFacet);

    newState.facetsQueryString = getFacetsQueryString(newFacets);

    resetPageNrAndRequestNewData({
      ...newState,
      data: { ...newState.data, facets: newFacets },
    });
  }

  function handleSearchChange(value: string) {
    setState({
      ...state,
      searchTerm: value,
    });
  }

  function handleSearchSubmit(event?: FormEvent<HTMLFormElement>) {
    if (event) {
      event.preventDefault();
    }

    const newState = {
      ...state,
      searchQueryString: getSearchQueryString(state.searchTerm),
    };

    resetPageNrAndRequestNewData(newState);
  }

  /** Passing the new state as an argument is needed, since otherwise the old state is used. **/
  function resetPageNrAndRequestNewData(newState: StateType, withScrollToTop?: boolean) {
    newState.pageNrQueryString = getPageNrQueryString(1);
    newState.data.pageNr = 1;

    setState(newState);
    handleDataReq(newState, withScrollToTop);
  }

  function handlePagination(pageNr: number) {
    const newState = {
      ...state,
      pageNrQueryString: getPageNrQueryString(pageNr),
      data: { ...state.data, pageNr },
    };

    setState(newState);
    handleDataReq(newState);
  }

  /** Passing the new state as an argument is needed, since otherwise the old state is used **/
  function handleDataReq(newState: StateType, withScrollToTop = true) {
    const lastActiveElement = document.activeElement;
    setLoading(true);
    fetch(endPointUrl, {
      method: "post",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        searchTerm: newState.searchTerm,
        pageNr: newState.data.pageNr,
        facets: newState?.data?.facets || null,
        itemsPerPage: newState.itemsPerPage,
        sortOrder: newState.sortOrder,
      }),
    })
      .then(function (response) {
        if (response.status >= 200 && response.status < 300) {
          return response.json();
        } else {
          console.log("Something went wrong");
        }
      })
      .then((json) => {
        newState = {
          ...newState,
          data: json,
        };

        setState(newState);
        historyPush(newState);

        if (withScrollToTop) {
          scrollToTop();
        }
      })
      .catch((err) => err)
      .finally(() => {
        setLoading(false);

        if (lastActiveElement && sidebarRef.current) {
          placeFocusInContainer(lastActiveElement, sidebarRef.current);
        }
      });
  }

  function scrollToTop() {
    if (sfrWrapperRef.current) {
      sfrWrapperRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }

  if (state.data == null) {
    return <div className="loading">&nbsp;</div>;
  }

  return (
    <section className="sfr__wrapper" ref={sfrWrapperRef}>
      <Search
        onChange={handleSearchChange}
        onSubmit={handleSearchSubmit}
        onSearchBoxSelectChange={handleFacetChange}
        showSearchBoxSelect={showSearchBoxSelect}
        searchTerm={state.searchTerm}
        facets={state.data.facets}
        resourcedTexts={resourcedTexts}
        loading={loading}
      />

      <ServerErrors errors={state.data.errors} />

      {renderSidebar && (
        <Sidebar
          facets={state.data.facets}
          onFacetChange={handleFacetChange}
          links={pageData.links}
          searchTerm={state.searchTerm}
          loading={loading}
          onClearFilter={handleClearFilter}
          resourcedTexts={resourcedTexts}
          ref={sidebarRef}
        />
      )}

      <div className="results-and-pagination">
        <Results
          results={state.data.results}
          resourcedTexts={resourcedTexts}
          sidebarIsActive={renderSidebar}
          loading={loading}
        />

        <Pagination
          pageNr={state.data.pageNr}
          totalResults={state.data.totalResults}
          itemsPerPage={state.itemsPerPage}
          onPageClick={handlePagination}
          resourcedTexts={resourcedTexts}
          loading={loading}
        />
      </div>
    </section>
  );
}
